import React, { useEffect, useState } from "react";
import { formatoPy } from "../../../utils/formatoNumeros";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

export default function VerRemito({
  data,
  onClose = () => {},
  esCliente = false,
}) {
  // Estado para la lista de productos (detalles)
  const [detalles, setDetalles] = useState([]);
  const [loadingDetalles, setLoadingDetalles] = useState(false);

  // Helper para formatear números simples
  const fmtNum = (v) => (v ? Number(v).toLocaleString("es-PY") : "-");

  // Efecto para cargar los detalles al abrir el modal
  useEffect(() => {
    if (!data?.id) return;

    setDetalles([]);
    setLoadingDetalles(true);
    // Llamamos al endpoint que filtra por remitoId
    appFetchJson(apiRoutes.remitoDetalles.list(data.id))
      .then((resp) => {
        // Asumiendo que el backend devuelve un objeto paginado { items: [...] } o lista directa
        const lista = resp.items || resp.content || resp || [];
        setDetalles(lista);
      })
      .catch((err) => console.error("Error cargando detalles:", err))
      .finally(() => setLoadingDetalles(false));
  }, [data]);

  if (!data) return null;

  // Filas de cabecera: Pin EE.SS. solo se incluye para admin
  const filasCabecera = [
    ["Cliente", data.clienteNombre],
    ["Tarjeta", data.tarjetaNumero],
    ["EESS", data.eessNombre],
    ["Estado", data.estadoNombre],
    ["Nro. Remito", data.codigo],
    ["Nro. Boleta", data.numeroBoleta],
    ["Valor Venta", formatoPy(data.valorVenta)],
    ["Fecha", data.dataFecha],
    ["Tipo", data.descricaoTipo],
    ["Kilometraje", fmtNum(data.numeroKilometraje)],
    // Cliente NO ve Pin EE.SS.; admin sí.
    ...(esCliente ? [] : [["Pin EE.SS.", data.numeroPinEess ?? "-"]]),
    // Cliente NO ve Pin Chofer; admin sí.
    ...(esCliente ? [] : [["Pin Chofer", data.numeroPinChofer ?? "-"]]),
    ["Liquidacion Ejecucion", data.liquidacionEjecucionId],
  ];

  return (
    <>
      {/* Encabezado */}
      <div className="modal-header">
        <h2 className="section-title">Datos de Remito</h2>
      </div>

      {/* Contenido (Scrolleable verticalmente) */}
      <div className="modal-body overflow-y-auto max-h-[70vh] p-4">
        {/* --- Sección 1: Cabecera del Remito --- */}
        <div className="form-grid mb-6 border-b pb-6">
          {filasCabecera.map(([label, val]) => (
            <Fila key={label} label={label} value={val} />
          ))}
        </div>

        {/* --- Sección 2: Tabla de Detalles (Productos) ---
            Se muestra para ambos actores.
            - Admin: todas las columnas (Remito, Producto, Cant., Precio, IVA, ...).
            - Cliente: solo Producto, Cant., Precio. */}
        <h3 className="text-md font-bold text-gray-700 mb-3 pl-1 border-l-4 border-green-600">
          Detalles de Productos
        </h3>

        {loadingDetalles ? (
          <div className="text-center py-4 text-gray-500">
            Cargando productos...
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-lg shadow-sm">
            <table className="table-card table-xs w-full">
              <thead className="bg-green-700 text-white">
                <tr>
                  {/* Columna "Remito" solo para admin */}
                  {!esCliente && (
                    <th className="px-2 py-2 text-left whitespace-nowrap">
                      Remito
                    </th>
                  )}
                  <th className="px-2 py-2 text-left whitespace-nowrap">
                    Producto
                  </th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">
                    Cant.
                  </th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">
                    Precio
                  </th>
                  {/* Resto de columnas solo para admin */}
                  {!esCliente && (
                    <>
                      <th className="px-2 py-2 text-left whitespace-nowrap">
                        IVA
                      </th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">
                        Valor IVA
                      </th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">
                        Bonif.
                      </th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">
                        Desc. Petr
                      </th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">
                        Desc. EESS
                      </th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">
                        Desc. Libe
                      </th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">
                        Pr Bonif.
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {detalles.length === 0 ? (
                  <tr>
                    <td
                      colSpan={esCliente ? 3 : 11}
                      className="text-center py-4 text-gray-500 italic"
                    >
                      No se encontraron items.
                    </td>
                  </tr>
                ) : (
                  detalles.map((row, idx) => (
                    <tr key={row.id || idx} className="hover:bg-gray-50">
                      {/* Columna "Remito" solo para admin (usa el código del padre) */}
                      {!esCliente && (
                        <td className="px-2 py-1 whitespace-nowrap font-medium text-gray-600">
                          {data.codigo}
                        </td>
                      )}
                      <td className="px-2 py-1 whitespace-nowrap">
                        {row.productoNombre}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {fmtNum(row.qnProducto)}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {formatoPy(row.valorPrecio)}
                      </td>
                      {/* Resto de columnas solo para admin */}
                      {!esCliente && (
                        <>
                          <td className="px-2 py-1">{row.ivaNombre}</td>
                          <td className="px-2 py-1 text-right">
                            {formatoPy(row.valorIva)}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {formatoPy(row.valorBonificacion)}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {formatoPy(row.valorDescPetr)}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {formatoPy(row.valorDescEess)}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {formatoPy(row.valorDescLibe)}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {row.prBonificacion}
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Contador simple al pie de la tabla */}
        {!loadingDetalles && detalles.length > 0 && (
          <div className="text-center text-xs text-gray-500 mt-2">
            {detalles.length} item{detalles.length !== 1 ? "s" : ""} encontrado
            {detalles.length !== 1 ? "s" : ""}.
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="modal-footer">
        <div className="flex justify-end">
          <button onClick={onClose} className="btn-fil">
            Cerrar
          </button>
        </div>
      </div>
    </>
  );
}

// Componente auxiliar para las filas de cabecera
function Fila({ label, value }) {
  return (
    <div className="form-row">
      <div className="form-label font-semibold text-gray-700">{label}:</div>
      <div className="value text-gray-900">{value ?? "-"}</div>
    </div>
  );
}
